import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCuratorChat } from '@/hooks/useCuratorChat';
import { hasTrainerSection } from '@/lib/storeTabs';
import { teacherAvatarOf, teacherNameOf } from '@/lib/studentFields';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';

export type LessonNavSection = {
  id: string;
  title: string;
  is_homework?: boolean;
};

/**
 * Правая колонка экрана урока (макет «Desktop Web v2»): разделы этого урока с
 * состояниями, обращение к куратору и переход в тренажёр.
 *
 * Отдельный файл, а не правка общего `SectionsNav`: тем же списком пользуется
 * конструктор урока в админке, и перекрашивать его ради ученика незачем.
 */
export function LessonSidebar({
  sections,
  activeId,
  completed,
  onSelect,
}: {
  sections: LessonNavSection[];
  activeId?: string;
  completed: Set<string>;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { curatorChat, showCuratorEntry } = useCuratorChat();
  const curator = teacherNameOf(curatorChat);
  const avatar = teacherAvatarOf(curatorChat);
  // Переход в тренажёры — только тому, у кого есть их раздел (02.10.2026): бесплатник тренажёров не видит вообще,
  // а маршрут `/app/trainers` для него закрыт. Сторож — `app/freeUserNoTrainers.test.ts`.
  const showTrainers = hasTrainerSection(useAuthStore((s) => s.user));
  const doneCount = sections.filter((s) => completed.has(s.id)).length;

  return (
    <div>
      <div className="mx-0.5 mb-2.5 flex items-baseline justify-between">
        <h2 className="font-display text-[15.5px] font-bold text-on-surface">{t('lessonPlayer.inThisLesson')}</h2>
        <span className="text-[12.5px] font-semibold text-on-surface-variant tabular-nums">
          {doneCount}/{sections.length}
        </span>
      </div>

      <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
        {sections.map((s, i) => {
          const done = completed.has(s.id);
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id)}
              className={cn(
                'flex w-full items-center gap-3 border-b border-outline py-3 text-left last:border-b-0',
                // ⚠️ Ширина с прибавкой, а не просто отрицательные поля. `w-full`
                // считается от ширины СОДЕРЖИМОГО, поэтому строка со сдвигом на
                // 18 точек влево не доставала до правого края ровно на 36: подсветка
                // выглядела съехавшей (замерено 02.09.2026).
                active && '-mx-[18px] w-[calc(100%+36px)] rounded-[18px] border-b-0 bg-primary-container px-[18px] py-3.5',
              )}
            >
              <span
                className={cn(
                  'grid flex-none place-items-center rounded-full text-[11.5px] font-bold',
                  active ? 'h-7 w-7 bg-primary text-on-primary' : 'h-[26px] w-[26px]',
                  !active && done && 'bg-info text-white',
                  !active && !done && 'bg-outline text-on-surface-variant',
                )}
              >
                {active ? (
                  <span className="material-symbols-outlined text-[12px]">play_arrow</span>
                ) : done ? (
                  <span className="material-symbols-outlined text-[13px]">check</span>
                ) : (
                  i + 1
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate text-[13px] text-on-surface', active ? 'font-bold' : 'font-semibold')}>
                  {s.is_homework ? t('lessonPlayer.homeworkTitle') : s.title}
                </span>
                <span
                  className={cn(
                    'block text-[11.5px]',
                    active ? 'text-on-primary-container' : 'text-on-surface-variant',
                  )}
                >
                  {active
                    ? t('lessonPlayer.watchingNow')
                    : done
                      ? t('lessons.done')
                      : s.is_homework
                        ? t('lessonPlayer.curatorChecks')
                        : t('lessonPlayer.notStarted')}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Спросить куратора — прямо из урока, а не через отдельный раздел.
          ⚠️ Только при праве chat и назначенном кураторе (решение владельца 14.09.2026). */}
      {showCuratorEntry && (
      <Link
        to="/app/chat"
        className="mt-3.5 flex items-center gap-3 rounded-[20px] bg-surface-container px-[18px] py-[15px] transition hover:bg-surface-container-high"
      >
        {/* ⚠️ Фото, если оно есть. До 06.09.2026 сервер его не отдавал вовсе,
            и ученик всегда видел буквы. Буквы остаются запасным вариантом:
            фото есть у одного куратора из 69, и это норма, а не сбой. */}
        <span className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-full bg-info-container text-[12px] font-bold text-info">
          {avatar ? (
            <img src={avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            (curator ?? t('chat.curator')).trim().split(/\s+/).map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase()
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-on-surface">{t('lessonPlayer.stuck')}</span>
          <span className="block truncate text-[11.5px] text-on-surface-variant">
            {t('lessonPlayer.askCurator', { name: curator ?? t('chat.curator') })}
          </span>
        </span>
        <span className="material-symbols-outlined flex-none text-[16px] text-on-surface-variant">chevron_right</span>
      </Link>
      )}

      {showTrainers && (
      <Link
        to="/app/trainers"
        className="mt-3 flex items-center gap-3 rounded-[20px] bg-surface-container px-[18px] py-[15px] transition hover:bg-surface-container-high"
      >
        <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-primary-container">
          <span className="material-symbols-outlined text-[19px] text-primary">smart_toy</span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-on-surface">{t('lessonPlayer.drillSkill')}</span>
          <span className="block text-[11.5px] text-on-surface-variant">{t('dashboard.tiles.allTrainers')}</span>
        </span>
        <span className="material-symbols-outlined flex-none text-[16px] text-on-surface-variant">chevron_right</span>
      </Link>
      )}
    </div>
  );
}
